import { useState } from "react";
import { useAdminAuditLogs } from "../../hooks/use-admin.js";
import { Skeleton } from "../../components/skeleton.js";

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IE") + " " + d.toLocaleTimeString("en-IE");
}

function actionLabel(action: string): string {
  return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseDetails(json: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(json) as Record<string, unknown>;
    if (Object.keys(parsed).length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function AdminAuditLogPage() {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const limit = 50;

  const { data, isLoading } = useAdminAuditLogs({ page, limit });

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="product-page">
      <h1 className="page-title">Audit Log</h1>
      <p className="text-muted text-sm mb-3">
        Every admin action is recorded. {total > 0 && `${total} entries total.`}
      </p>

      {isLoading && <Skeleton />}

      {!isLoading && logs.length === 0 && (
        <p className="text-muted">No audit log entries yet.</p>
      )}

      {!isLoading && logs.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Admin</th>
                  <th>Target</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((entry) => {
                  const details = parseDetails(entry.details_json);
                  const isExpanded = expandedId === entry.id;

                  return (
                    <tr key={entry.id}>
                      <td className="text-sm text-nowrap">
                        {fmtDate(entry.occurred_at)}
                      </td>
                      <td>
                        <span className="badge badge--neutral">
                          {actionLabel(entry.action)}
                        </span>
                      </td>
                      <td className="text-sm text-mono">
                        {entry.admin_user_id.length > 12
                          ? entry.admin_user_id.slice(0, 12) + "\u2026"
                          : entry.admin_user_id}
                      </td>
                      <td className="text-sm">
                        {entry.target_user_id && (
                          <span title={entry.target_user_id}>
                            User: {entry.target_user_id.slice(0, 8)}&hellip;
                          </span>
                        )}
                        {entry.target_org_id && (
                          <span title={entry.target_org_id}>
                            Org: {entry.target_org_id.slice(0, 8)}&hellip;
                          </span>
                        )}
                        {!entry.target_user_id && !entry.target_org_id && (
                          <span className="text-muted">&mdash;</span>
                        )}
                      </td>
                      <td>
                        {details ? (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() =>
                              setExpandedId(isExpanded ? null : entry.id)
                            }
                          >
                            {isExpanded ? "Hide" : "View"}
                          </button>
                        ) : (
                          <span className="text-muted text-sm">&mdash;</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Expanded detail rows rendered below the table */}
          {expandedId &&
            (() => {
              const entry = logs.find((e) => e.id === expandedId);
              if (!entry) return null;
              const details = parseDetails(entry.details_json);
              if (!details) return null;
              return (
                <div className="admin-detail-panel mt-2 mb-3">
                  <h3 className="text-sm fw-600 mb-1">
                    {actionLabel(entry.action)} &mdash;{" "}
                    {fmtDate(entry.occurred_at)}
                  </h3>
                  <pre className="code-block text-xs">
                    {JSON.stringify(details, null, 2)}
                  </pre>
                </div>
              );
            })()}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination mt-3">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="text-sm px-2">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
